import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Form, Row, Alert, Col, Container } from 'react-bootstrap';
import Loadable from '@loadable/component';

import './calculator.styles.scss';

const Pulse = Loadable(() => import('react-reveal/Pulse'));

const CalculatorSchema = Yup.object().shape({
  employeeCount: Yup.number()
    .typeError('Employee count must be a number')
    .min(1, 'Unrealistic employee count')
    .max(500000, 'Employee count too large')
    .required('Required'),
  averageEmployeeWage: Yup.number()
    .typeError('Average employee wage per year must be a number')
    .min(12000, 'Employee wage per annum is below the legal minimum wage')
    .required('Required'),
});

const Calculator: React.FC = () => {
  const [retentionCost, setRetentionCost] = useState(null);
  const [formError, setFormError] = useState();
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'CAD',
  });
  const {
    handleChange,
    handleBlur,
    handleSubmit,
    errors,
    touched,
    values,
  } = useFormik({
    validationSchema: CalculatorSchema,
    initialValues: { employeeCount: '100', averageEmployeeWage: '60000' },
    onSubmit: async ({ employeeCount, averageEmployeeWage }) => {
      try {
        setFormError(undefined);
        setRetentionCost(
          currencyFormatter.format(
            Number(employeeCount) *
              0.16 *
              (Number(averageEmployeeWage) * 0.5 * 0.23)
          )
        );
      } catch (error) {
        setFormError(error.message);
      }
    },
  });

  return (
    <Pulse top>
      <div className="calculator res-pt-0 pb-100 pt-70">
        <Container fluid>
          <Row className="align-items-center">
            <Col lg={6} md={12}>
              <div className="our-mission-content">
                <div className="content">
                  <h2>Cost Retention Calculator</h2>
                  <p>
                    The repercussions of a bad commute go beyond just the
                    negative impacts on employee wellness. Not taking action is
                    costing companies a lot more than they think.
                  </p>
                </div>
              </div>
            </Col>

            <div className="col-lg-6 col-md-12">
              <div className="calculator-content">
                <div className="content">
                  <div className="content">
                    <h4>What is a bad commute costing you?</h4>
                    {formError && <Alert variant="danger">{formError}</Alert>}
                    {retentionCost && (
                      <Alert variant="warning">
                        {retentionCost} is the cost of retention
                      </Alert>
                    )}
                    <Form onSubmit={handleSubmit}>
                      <div className="contact-form">
                        <Row>
                          <Form.Group
                            as={Col}
                            lg="12"
                            md="12"
                            controlId="employeeCountValidation"
                          >
                            <Form.Label>Employee Count</Form.Label>
                            <Form.Control
                              name="employeeCount"
                              type="text"
                              value={values.employeeCount}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isValid={
                                touched.employeeCount && !errors.employeeCount
                              }
                              isInvalid={!!errors.employeeCount}
                              required
                            />
                            {errors.employeeCount && (
                              <Form.Control.Feedback type="invalid">
                                {errors.employeeCount}
                              </Form.Control.Feedback>
                            )}
                          </Form.Group>
                          <Form.Group
                            as={Col}
                            lg="12"
                            md="12"
                            controlId="averageEmployeeWageValidation"
                          >
                            <Form.Label>
                              Average Annual Employee Wage
                            </Form.Label>
                            <Form.Control
                              name="averageEmployeeWage"
                              type="text"
                              value={values.averageEmployeeWage}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isValid={
                                touched.averageEmployeeWage &&
                                !errors.averageEmployeeWage
                              }
                              isInvalid={!!errors.averageEmployeeWage}
                              required
                            />
                            {errors.averageEmployeeWage && (
                              <Form.Control.Feedback type="invalid">
                                {errors.averageEmployeeWage}
                              </Form.Control.Feedback>
                            )}
                          </Form.Group>

                          <Col lg={4} md={12}>
                            <button type="submit" className="default-btn">
                              <i className="flaticon-analytics"></i>
                              Calculate <span></span>
                            </button>
                          </Col>
                        </Row>
                      </div>
                    </Form>
                  </div>
                </div>
              </div>
            </div>
          </Row>
        </Container>
      </div>
    </Pulse>
  );
};

export default Calculator;
